<template>
  <div class="student-management">
    <el-card class="management-card">
      <div slot="header" class="card-header">
        <span>班级管理</span>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input v-model="filterForm.studentName" placeholder="输入学生姓名"></el-input>
          </el-col>
          <el-col :span="8">
            <el-input v-model="filterForm.studentId" placeholder="输入学号"></el-input>
          </el-col>
          <el-col :span="8">
            <el-button type="primary" @click="fetchStudents">查询</el-button>
            <el-button type="warning" @click="resetFilter">重置</el-button>
            <el-button @click="showAddStudentDialog">录入</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 学生表格 -->
      <el-table
        v-loading="loading"
        :data="students"
        border
        style="width: 100%"
        class="students-table"
      >
        <el-table-column type="index" label="序号" width="100"></el-table-column>
        <el-table-column prop="studentId" label="学生ID" width="150"></el-table-column>
        <el-table-column prop="studentNumber" label="学号" width="180"></el-table-column>
        <el-table-column prop="name" label="姓名" width="180"></el-table-column>
        <el-table-column prop="gender" label="性别" width="150"></el-table-column>
        <el-table-column prop="className" label="班级" width="200"></el-table-column>
        <el-table-column prop="contactInfo" label="联系电话" width="220"></el-table-column>
        <el-table-column label="操作">
          <template #default="scope">
            <el-button type="primary" size="small" @click="showEditStudentDialog(scope.row)">编辑</el-button>
            <el-button type="danger" size="small" @click="deleteStudent(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 添加/编辑学生对话框 -->
    <el-dialog v-model="studentDialogVisible" :title="isEditingStudent ? '编辑学生' : '添加学生'" width="600px">
      <el-form
        ref="studentForm"
        :model="currentStudent"
        :rules="studentRules"
        label-width="120px"
      >
        <el-form-item label="学号" prop="studentId" :disabled="isEditingStudent">
          <el-input v-model="currentStudent.studentId" placeholder="请输入学号"></el-input>
        </el-form-item>
        <el-form-item label="姓名" prop="name">
          <el-input v-model="currentStudent.name" placeholder="请输入姓名"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-select v-model="currentStudent.gender" placeholder="请选择性别">
            <el-option label="男" value="男"></el-option>
            <el-option label="女" value="女"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="年级" prop="grade">
          <el-input v-model="currentStudent.grade" placeholder="请输入年级"></el-input>
        </el-form-item>
        <el-form-item label="班级" prop="className">
          <el-input v-model="currentStudent.className" placeholder="请输入班级"></el-input>
        </el-form-item>
        <el-form-item label="联系电话" prop="contactInfo">
          <el-input v-model="currentStudent.contactInfo" placeholder="请输入联系电话"></el-input>
        </el-form-item>
        <el-form-item label="电子邮箱" prop="email">
          <el-input v-model="currentStudent.email" placeholder="请输入电子邮箱"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="studentDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitStudentForm">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request';

const { proxy } = getCurrentInstance();

// 加载状态
const loading = ref(false);

// 筛选条件
const filterForm = reactive({
  studentName: '',
  studentId: ''
});

// 表格数据
const students = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框状态
const studentDialogVisible = ref(false);
const isEditingStudent = ref(false);
const currentStudent = reactive({
  id: '',
  studentId: '',
  name: '',
  gender: '',
  grade: '',
  className: '',
  contactInfo: '',
  email: ''
});

// 表单验证规则
const studentRules = {
  studentId: [
    { required: true, message: '请输入学号', trigger: 'blur' }
  ],
  name: [
    { required: true, message: '请输入姓名', trigger: 'blur' }
  ],
  gender: [
    { required: true, message: '请选择性别', trigger: 'change' }
  ],
  grade: [
    { required: true, message: '请输入年级', trigger: 'blur' }
  ],
  className: [
    { required: true, message: '请输入班级', trigger: 'blur' }
  ],
  contactInfo: [
    { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1\d*$/, message: '请输入以1开头的手机号码', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入电子邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
  ]
};

// 获取学生列表
const fetchStudents = async () => {
  try {
    loading.value = true;
    // 从localStorage中验证用户已登录
    const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
    
    if (!userInfo) {
      console.error('用户未登录');
      ElMessage.error('请先登录教师账号');
      loading.value = false;
      return;
    }
    
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      studentName: filterForm.studentName || undefined,
      // 修改为后端期望的参数名studentNumber
      studentNumber: filterForm.studentId || undefined
    };

    console.log('发送请求参数:', params);
    console.log('发送请求URL:', '/teacher/students');
    
    const response = await request.get('/teacher/students', params);
    
    console.log('请求响应:', response);
    students.value = response.data?.records || [];
    total.value = response.data?.total || 0;
  } catch (error) {
    console.error('获取学生列表失败:', error);
    console.error('错误详情:', error.response ? error.response.data : error.message);
    ElMessage.error('获取学生列表失败');
  } finally {
    loading.value = false;
  }
};

// 重置筛选
const resetFilter = () => {
  filterForm.studentName = '';
  filterForm.studentId = '';
  currentPage.value = 1;
  fetchStudents();
};

// 分页处理
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchStudents();
};

const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchStudents();
};

// 显示添加学生对话框
const showAddStudentDialog = () => {
  isEditingStudent.value = false;
  Object.keys(currentStudent).forEach(key => {
    currentStudent[key] = '';
  });
  studentDialogVisible.value = true;
};

// 显示编辑学生对话框
const showEditStudentDialog = (student) => {
  isEditingStudent.value = true;
  
  // 1. 清空currentStudent
  Object.keys(currentStudent).forEach(key => {
    currentStudent[key] = '';
  });
  
  // 2. 复制student对象数据到currentStudent
  Object.assign(currentStudent, student);
  
  // 3. 确保学生ID和学号字段正确映射
  // 保存原始学生ID到currentStudent.id
  if (student.id) {
    currentStudent.id = student.id;
  } else if (student.studentId) {
    currentStudent.id = student.studentId;
  }
  
  // 确保学号字段显示正确的值（使用studentNumber作为学号）
  if (student.studentNumber) {
    currentStudent.studentId = student.studentNumber;
  }
  
  // 确保联系电话字段显示正确的值
  if (student.contactInfo) {
    currentStudent.contactInfo = student.contactInfo;
  }
  
  console.log('显示编辑对话框时的学生数据:', {
    'student.id': student.id,
    'student.studentId': student.studentId,
    'student.studentNumber': student.studentNumber,
    'currentStudent.id': currentStudent.id,
    'currentStudent.studentId': currentStudent.studentId
  });
  studentDialogVisible.value = true;
};

// 提交学生表单
const submitStudentForm = async () => {
  try {
    const form = proxy.$refs.studentForm;
    await form.validate();

    console.log('表单验证通过，准备提交数据');
    console.log('提交的学生数据:', JSON.stringify(currentStudent, null, 2));

    // 创建符合后端要求的数据格式 - 重新构建对象确保字段正确映射
    const studentData = {
      id: currentStudent.id,
      name: currentStudent.name,
      gender: currentStudent.gender,
      grade: currentStudent.grade,
      className: currentStudent.className,
      email: currentStudent.email,
      // 后端期望的学号字段名是studentNumber
      studentNumber: currentStudent.studentId,
      // 联系电话直接使用contactInfo
      contactInfo: currentStudent.contactInfo
    };

    console.log('转换后发送给后端的数据:', JSON.stringify(studentData, null, 2));

    if (isEditingStudent.value) {
      // 编辑学生
      // 添加更健壮的学生ID获取逻辑，确保有有效的ID值
      console.log('编辑时的currentStudent对象:', JSON.stringify(currentStudent, null, 2));
      
      // 必须使用currentStudent.id作为学生ID，不能使用学号(studentId)
      let studentId = currentStudent.id;
      
      // 确保studentId有效
      if (!studentId) {
        ElMessage.error('无法获取有效的学生ID，请重试');
        return;
      }
      
      console.log('编辑学生ID:', studentId);
      
      const response = await request.put(`/teacher/students/${studentId}`, studentData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('编辑学生响应:', response);
      if (response.code === 200 || response.data?.success) {
        ElMessage.success('学生信息更新成功');
        studentDialogVisible.value = false;
        fetchStudents();
      } else {
        ElMessage.error(`学生信息更新失败: ${response.message || '未知错误'}`);
      }
    } else {
      // 添加学生
      console.log('开始添加新学生，调用API:', '/api/teacher/students');
      const startTime = Date.now();
      const response = await request.post('/teacher/students', studentData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const endTime = Date.now();
      console.log('添加学生API调用耗时:', endTime - startTime, 'ms');
      console.log('添加学生完整响应:', response);
      
      // 详细检查响应结构
      console.log('响应状态码:', response.code);
      console.log('响应消息:', response.message);
      console.log('响应数据:', response.data);
      
      if (response.code === 200 || response.data?.success) {
        ElMessage.success('学生添加成功');
        studentDialogVisible.value = false;
        fetchStudents();
      } else {
        ElMessage.error(`学生添加失败: ${response.message || '未知错误'}`);
      }
    }
  } catch (error) {
    console.error('提交表单失败:', error);
    console.error('错误详情:', error.response ? error.response.data : error.message);
    // 检查是否是认证错误
    if (error.response && error.response.status === 401) {
      ElMessage.error('认证已过期，请重新登录');
      // 跳转到登录页
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } else {
      ElMessage.error('提交表单失败，请重试');
    }
  }
};

// 删除学生
const deleteStudent = async (student) => {
  try {
    await ElMessageBox.confirm('确定要删除这名学生吗？', '确认删除', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    // 添加更健壮的学生ID获取逻辑，确保有有效的ID值
    console.log('删除时的student对象:', JSON.stringify(student, null, 2));
    
    // 重要：必须使用真正的学生ID字段，不能使用学号(studentNumber)
    // 1. 优先使用id字段
    // 2. 如果id为空，检查是否有其他可能的ID字段
    let studentId = student.id;
    
    console.log('尝试获取学生ID:', studentId);
    
    // 增强的ID有效性检查
    if (!studentId || studentId.toString().includes('S') || isNaN(studentId)) {
      // 如果ID无效或看起来像学号(以S开头或不是数字)，尝试从student对象中查找其他可能的ID字段
      // 检查常见的ID字段命名
      const possibleIdFields = ['id', 'studentId', 'student_id', 'userId', 'user_id'];
      let foundValidId = false;
      
      for (const field of possibleIdFields) {
        if (student[field] && !student[field].toString().includes('S') && !isNaN(student[field])) {
          studentId = student[field];
          foundValidId = true;
          console.log(`找到有效的学生ID字段 ${field}:`, studentId);
          break;
        }
      }
      
      if (!foundValidId) {
        console.error('未找到有效的学生ID字段:', possibleIdFields);
        ElMessage.error('无法获取有效的学生ID，请刷新页面后重试');
        return;
      }
    }
    
    console.log('最终使用的学生ID:', studentId);
    
    const response = await request.delete(`/teacher/students/${studentId}`);
    if (response.code === 200 || response.data?.success) {
      ElMessage.success('学生删除成功');
      fetchStudents();
    } else {
      ElMessage.error(`学生删除失败: ${response.message || '未知错误'}`);
    }
  } catch (error) {
    if (error.name !== 'Error') {
      console.error('删除学生失败:', error);
      // 检查是否是认证错误
      if (error.response && error.response.status === 401) {
        ElMessage.error('认证已过期，请重新登录');
        // 跳转到登录页
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        ElMessage.error('删除学生失败，请重试');
      }
    }
  }
};


onMounted(() => {
  fetchStudents();
});
</script>

<style scoped>
.student-management {
  padding: 10px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-container {
      margin-bottom: 20px;
      padding: 15px;
      background-color: #f9f9f9;
      border-radius: 4px;
    }

    .students-table {
      margin-bottom: 20px;
      --el-table-row-height: 80px; /* 将行高设置为原来的两倍 */
      font-size: 16px; /* 增大字体大小 */
    }

    .pagination-container {
      display: flex;
      justify-content: flex-end;
      margin-top: 15px;
    }
</style>